import { SuspenseSelect } from '@/components/Suspense';
import {
  BaseSelectProps,
  useSetCacheQueryData,
} from '@/context/SelectComponent/Base';
import {
  BaseOption,
  useModelsToOptionsWithCacheItems,
} from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import {
  FormController,
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import {
  CrItems as CrItemsModel,
  useGetCrSubItemsByCrItemSuspenseInfinite,
} from 'ap-openapi';
import classnames from 'classnames/bind';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import styles from './CrSubItems.module.scss';
const cx = classnames.bind(styles);

export type Props<TFieldValues extends FieldValues> = {
  crItemId?: PathValue<TFieldValues, Path<TFieldValues>>;
  selectProps?: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'comboBox'
  >;
  query?: string;
  per?: number;
  validateMaxCount?: number;
  comboBox?: boolean;
  setLabelField?: (label: string) => void;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'> &
  BaseSelectProps;

export const _CrSubItemsSelect = <TFieldValues extends FieldValues>({
  crItemId,
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  onChangeAfter,
  setLabelField,
  cacheQueryKey,
  per = 25,
  comboBox = true,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetCrSubItemsByCrItemSuspenseInfinite(
    {
      query,
      item_id: crItemId,
      per: per,
    },
    {
      query: {
        getNextPageParam: (page) => {
          const url = new URL(page.data.next ?? '/', location.href);
          if (!url.searchParams.has('page')) return undefined;
          return Number(url.searchParams.get('page'));
        },
      },
    }
  );
  const data = useMemo(
    () =>
      getPageData(apiData, 'cr_sub_items').filter((item) => crItemId && item),
    [apiData, crItemId]
  );
  const options = useModelsToOptionsWithCacheItems<CrItemsModel>(
    data,
    cacheQueryKey,
    query,
    defaultOptions
  );

  const fixedNoSelectLabelOption = useMemo(
    () => [
      ...options,
      {
        value: null,
        label: t('cr_sub_items_input_no_select_item'),
      },
    ],
    [options, t]
  );
  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((search: string) => {
    startTransition(() => {
      setQuery(search);
    });
  }, []);
  const selectProps: SelectProps = useMemo(
    () => ({
      onSearchDebounceWait: 600,
      comboBox: comboBox,
      placeholder: t('cr_sub_items_placeholder'),
      inputPlaceholder: t('cr_sub_items_input_placeholder'),
      ..._selectProps,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      options: fixedNoSelectLabelOption as BaseOption[],
      loading: isLoading,
      onSearch: onSearch,
      fetchMore: fetchMore,
      mode: 'single',
      selectAll: undefined,
      selectAllLabel: undefined,
    }),
    [
      comboBox,
      t,
      _selectProps,
      fixedNoSelectLabelOption,
      isLoading,
      onSearch,
      fetchMore,
    ]
  );

  useSetCacheQueryData(cacheQueryKey, data);

  const wrapOnChangeAfter = useCallback(
    (value: string | number | (string | number)[]) => {
      const selectedOption = fixedNoSelectLabelOption.find(
        (option) => option.value === String(value)
      );
      onChangeAfter?.(value);
      setLabelField?.(selectedOption?.label ?? '');
    },
    [fixedNoSelectLabelOption, setLabelField, onChangeAfter]
  );

  // fixedNoSelectLabelOption[0] is {value: null, label: t('cr_sub_items_input_no_select_item')}
  return query || (fixedNoSelectLabelOption.length > 1 && crItemId) ? (
    <FormSelect
      selectProps={selectProps}
      {...rest}
      onChangeAfter={wrapOnChangeAfter}
    />
  ) : (
    <FormController control={rest.control} name={rest.name}>
      {(...rest) => (
        <>
          <div className={cx(styles['disabled-input'])}>
            {t('cr_sub_items_placeholder')}
          </div>
          {/* value set from CrItemsSelect */}
          <input hidden {...rest} />
        </>
      )}
    </FormController>
  );
};
_CrSubItemsSelect.displayName = '_CrSubItemsSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const CrSubItemsSelect = memo((props) => (
  <SuspenseSelect>
    <_CrSubItemsSelect {...props} />
  </SuspenseSelect>
)) as typeof _CrSubItemsSelect;
CrSubItemsSelect.displayName = 'CrSubItemsSelect';
